import { convertToStandardErrorAndThrow, RuntimeError, ValidationError } from '@multiplier/lib-shared-errors';
import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { apiGet } from '@/lib/api';
import { QUERY_KEYS } from '@/lib/query-utils';

/**
 * Mirror of tax-api's `FeatureFlagsResponseSchema`. Kept narrow on purpose —
 * the backend response schema is explicit per-flag, so this stays in sync
 * one flag at a time rather than drifting through a `Record<string, ...>` widening.
 */
export interface FeatureFlags {
  tax_test_flag: boolean;
  tax_file_browser: boolean;
  tax_fbar_generator: boolean;
}

export interface FeatureFlagsResponse {
  flags: FeatureFlags;
}

export const useFeatureFlagsQuery = () => {
  const { selectedOrganizationId: organizationId } = useOrganization();

  return useQuery<FeatureFlagsResponse>({
    queryKey: QUERY_KEYS.FeatureFlags({ organizationId }),
    queryFn: async () => {
      if (!organizationId) {
        throw new ValidationError('Organisation ID is required to fetch feature flags');
      }

      try {
        return await apiGet<FeatureFlagsResponse>(`/api/organizations/${organizationId}/feature-flags`);
      } catch (error) {
        convertToStandardErrorAndThrow(error, (e) => new RuntimeError('Failed to fetch feature flags', undefined, e));
      }
    },
    enabled: !!organizationId,
    refetchInterval: 15_000,
    meta: {
      showErrorToast: false,
    },
  });
};
